/**
 * 会話ウィンドウ。
 *
 * - 話者名 + 本文 + テキスト送り
 * - 本文中の用語はクリックできるようにハイライトする
 * - 選択肢は最終行と同時に出す
 */

import type { GlossaryEntry } from '../scenario/dataSource';
import type { DialogueView } from '../scenario/dialogueRunner';
import { clear, el } from './dom';
import { usesTouchControls } from './touchControls';
import { renderTextWithTerms, type TermSource } from './terms';

const SPEAKER_LABELS: Record<string, string> = {
  player: 'あなた',
  narration: '',
};

export class DialogueWindow {
  readonly root: HTMLElement;
  private speakerNode: HTMLElement;
  private textNode: HTMLElement;
  private choiceNode: HTMLElement;
  private nextHint: HTMLElement;
  private portrait: HTMLElement;
  private current: DialogueView | null = null;
  /** 1 文字ずつ表示している途中か */
  private typing = false;
  private typingTimer: number | null = null;
  private fullText = '';

  constructor(
    private readonly termSource: TermSource,
    private readonly onTermClick: (entry: GlossaryEntry | null, term: string) => void,
    private readonly onAdvance: () => void,
    private readonly onChoose: (index: number) => void,
    private readonly onType: () => void,
  ) {
    this.speakerNode = el('div', { class: 'dialogue-speaker' });
    this.textNode = el('div', { class: 'dialogue-text' });
    this.choiceNode = el('div', { class: 'dialogue-choices' });
    this.nextHint = el('div', { class: 'dialogue-next', text: usesTouchControls() ? '▼ タップで次へ' : '▼ クリック / Space / E' });
    this.portrait = el('div', { class: 'dialogue-portrait', hidden: true });

    const panel = el('div', { class: 'dialogue-panel' }, [
      this.portrait,
      this.speakerNode,
      this.textNode,
      this.choiceNode,
      this.nextHint,
    ]);
    this.root = el('div', { class: 'dialogue-layer', hidden: true }, [panel]);
    this.root.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      if (target.closest('.term') || target.closest('.dialogue-choice')) return;
      this.handleAdvance();
    });
  }

  get isOpen(): boolean {
    return !this.root.hidden;
  }

  /** 表示を更新する。null で閉じる */
  render(view: DialogueView | null): void {
    this.current = view;
    if (!view) {
      this.stopTyping();
      this.root.hidden = true;
      return;
    }
    this.root.hidden = false;

    const speaker = SPEAKER_LABELS[view.line.speaker] ?? view.line.speaker;
    this.speakerNode.textContent = speaker;
    this.speakerNode.hidden = speaker.length === 0;
    this.root.classList.toggle('is-narration', view.line.speaker === 'narration');
    const people = ['高瀬', '室伏', '大森', '佐伯', '葉山', 'リナ'];
    const portraitIndex = people.findIndex(name => speaker.startsWith(name));
    this.portrait.hidden = portraitIndex < 0;
    this.root.classList.toggle('has-portrait', portraitIndex >= 0);
    if (portraitIndex >= 0) {
      this.portrait.style.backgroundPosition = `${(portraitIndex % 3) * 50}% ${Math.floor(portraitIndex / 3) * 100}%`;
      this.portrait.setAttribute('role', 'img');
      this.portrait.setAttribute('aria-label', `${speaker}の顔写真`);
    }

    this.startTyping(view);
    this.renderChoices(view);
  }

  private renderChoices(view: DialogueView): void {
    clear(this.choiceNode);
    if (view.choices.length === 0) {
      this.choiceNode.hidden = true;
      return;
    }
    this.choiceNode.hidden = false;
    view.choices.forEach((choice, index) => {
      const node = el('button', {
        class: 'dialogue-choice',
        type: 'button',
        text: choice.text,
      });
      node.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.onChoose(index);
      });
      this.choiceNode.append(node);
    });
  }

  /** 1 文字ずつ表示。最後まで出たら用語ハイライト付きで描き直す */
  private startTyping(view: DialogueView): void {
    this.stopTyping();
    this.fullText = view.line.text;
    this.typing = true;
    this.setNextHintVisible(false);
    clear(this.textNode);

    let index = 0;
    const step = (): void => {
      index += 1;
      this.textNode.textContent = this.fullText.slice(0, index);
      if (index % 2 === 0) this.onType();
      if (index >= this.fullText.length) {
        this.finishTyping();
        return;
      }
      this.typingTimer = window.setTimeout(step, 22);
    };
    this.typingTimer = window.setTimeout(step, 22);
  }

  private finishTyping(): void {
    this.stopTyping();
    const view = this.current;
    if (!view) return;
    clear(this.textNode);
    this.textNode.append(
      renderTextWithTerms(this.fullText, view.line.terms, this.termSource, this.onTermClick),
    );
    this.setNextHintVisible(view.choices.length === 0);
  }

  /** Keep the footer's space while typing, so bottom-aligned text stays put. */
  private setNextHintVisible(visible: boolean): void {
    this.nextHint.style.visibility = visible ? 'visible' : 'hidden';
    this.nextHint.setAttribute('aria-hidden', String(!visible));
  }

  private stopTyping(): void {
    if (this.typingTimer !== null) {
      window.clearTimeout(this.typingTimer);
      this.typingTimer = null;
    }
    this.typing = false;
  }

  /** クリック / Space / E で進める。表示途中なら一気に出す */
  handleAdvance(): void {
    if (!this.isOpen) return;
    if (this.typing) {
      this.finishTyping();
      return;
    }
    if (this.current && this.current.choices.length > 0) return;
    this.onAdvance();
  }
}
